import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  deleteConnection,
  getUser,
  getUserConnections,
  respondConnection,
} from "../../api/api";
import { defaultPfpUrl } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { cn } from "../../lib/utils";
import SignedOutPrompt from "../SignedOutPrompt";
import PostConnectButton from "../feed_page/PostConnectButton";
import {
  NotificationEmptyState,
  NotificationErrorPanel,
} from "../notifications_page/NotificationTabShared";
import {
  useInboxSubscription,
  useUserRealtime,
} from "../../context/UserRealtimeContext";

const TABS = ["all", "sent", "received"];

/**
 * @param {string|null} raw
 * @returns {number}
 */
function parseConnectionIdParam(raw) {
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : 0;
}

/**
 * Move the matching connection to the front of the list when present.
 * @param {object[]} connections
 * @param {number} highlightedId
 * @returns {object[]}
 */
function pinHighlightedFirst(connections, highlightedId) {
  if (!highlightedId) {
    return connections;
  }
  const match = [];
  const rest = [];
  connections.forEach((connection) => {
    if (Number(connection.id) === highlightedId) {
      match.push(connection);
    } else {
      rest.push(connection);
    }
  });
  return [...match, ...rest];
}

/**
 * @param {number} [size]
 * @returns {JSX.Element}
 */
function ChevronIcon({ size = 14 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

/**
 * Vertical-dots glyph for the overflow trigger.
 * @param {{ className?: string }} [props]
 * @returns {JSX.Element}
 */
function IconMoreVertical({ className = "h-[18px] w-[18px]" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * @param {object|null|undefined} person
 * @returns {string}
 */
function displayName(person) {
  if (!person) return "Unknown user";
  const full = `${person.first_name || ""} ${person.last_name || ""}`.trim();
  return full || person.username || "Unknown user";
}

/**
 * @param {string} name
 * @returns {string}
 */
function initials(name) {
  return (name || "U")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/**
 * @param {object} connection
 * @param {number} userId
 * @returns {number}
 */
function peerIdFor(connection, userId) {
  return Number(connection.sender_id) === Number(userId)
    ? Number(connection.receiver_id)
    : Number(connection.sender_id);
}

/**
 * 3-dot menu with a Remove action.
 * @param {Object} props
 * @param {() => void} props.onRemove
 * @returns {JSX.Element}
 */
function RemoveOverflowMenu({ onRemove }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    /**
     * @param {MouseEvent|TouchEvent} event
     * @returns {void}
     */
    function handlePointerDown(event) {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [menuOpen]);

  return (
    <div className="relative shrink-0" ref={wrapRef}>
      <button
        type="button"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-text-muted hover:bg-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring"
        aria-label="More actions"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <IconMoreVertical />
      </button>
      {menuOpen ? (
        <div
          className={cn(
            "absolute right-0 z-30 min-w-[9rem] overflow-hidden rounded-md border border-ui-border bg-ui-card text-sm shadow-md",
            "top-full mt-1"
          )}
        >
          <div className="py-1">
            <button
              type="button"
              className="flex w-full items-center px-3 py-2 text-left text-delete hover:bg-highlight"
              onClick={() => {
                setMenuOpen(false);
                onRemove();
              }}
            >
              Remove
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * One connection / request row.
 * @param {Object} props
 * @param {object|null} props.person
 * @param {number} props.personId
 * @param {string} [props.meta]
 * @param {React.ReactNode} props.actions
 * @param {boolean} [props.highlighted]
 * @returns {JSX.Element}
 */
function ConnectionRow({ person, personId, meta, actions, highlighted = false }) {
  const name = displayName(person);
  const pfp = person?.pfp_url || defaultPfpUrl;
  const rowRef = useRef(null);

  useEffect(() => {
    if (!highlighted || !rowRef.current) {
      return undefined;
    }
    rowRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    return undefined;
  }, [highlighted]);

  return (
    <Card
      ref={rowRef}
      className={cn(
        "border-ui-border shadow-profile-sm transition-colors hover:border-accent",
        highlighted && "border-accent bg-tag-accent ring-2 ring-accent"
      )}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
            <Avatar className="h-16 w-16 shrink-0 border-2 border-ui-border">
              <AvatarImage
                src={pfp}
                alt=""
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = defaultPfpUrl;
                }}
              />
              <AvatarFallback className="text-text-secondary">
                {initials(name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <Link
                to={`/profile/${personId}`}
                className="text-lg font-semibold text-accent transition-colors hover:text-text-accent-hover"
              >
                {name}
              </Link>
              {person?.username ? (
                <p className="mt-0.5 text-sm text-text-muted">@{person.username}</p>
              ) : null}
              {meta ? <p className="mt-2 text-sm text-text-muted">{meta}</p> : null}
            </div>
          </div>
          <div className="flex shrink-0 items-center justify-end gap-2">{actions}</div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Manage connections: All, Sent, and Received tabs under Network.
 * @param {Object} props
 * @param {number} props.userId
 * @param {object|null} props.user
 * @returns {JSX.Element}
 */
function ManageConnectionsPage({ userId, user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const highlightedConnectionId = parseConnectionIdParam(searchParams.get("connection"));
  const tab = TABS.includes(requestedTab)
    ? requestedTab
    : highlightedConnectionId
      ? "received"
      : "all";

  const [connections, setConnections] = useState([]);
  const [peerUsers, setPeerUsers] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [acceptedIds, setAcceptedIds] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null);

  const { pendingConnectionCount, reportConnectionChange, refreshPendingConnections } =
    useUserRealtime();
  const prevPendingCountRef = useRef(pendingConnectionCount);

  const reloadConnections = useCallback(async () => {
    if (!userId) {
      setConnections([]);
      return;
    }
    const result = await getUserConnections(userId);
    if (result.success && Array.isArray(result.data)) {
      setConnections(result.data);
      setError(null);
    } else {
      setError(result.error || "Failed to load connections");
    }
  }, [userId]);

  useEffect(() => {
    reloadConnections();
  }, [reloadConnections]);

  useInboxSubscription(
    {
      onReconnect: () => {
        reloadConnections();
        refreshPendingConnections();
      },
    },
    Boolean(userId)
  );

  useEffect(() => {
    const prev = prevPendingCountRef.current;
    prevPendingCountRef.current = pendingConnectionCount;
    if (!userId || pendingConnectionCount <= prev) return;
    reloadConnections();
  }, [pendingConnectionCount, userId, reloadConnections]);

  const peerIdsKey = useMemo(() => {
    const ids = connections.map((conn) => peerIdFor(conn, userId)).filter(Number.isFinite);
    return [...new Set(ids)].sort((a, b) => a - b).join(",");
  }, [connections, userId]);

  useEffect(() => {
    if (!userId || !peerIdsKey) {
      return undefined;
    }
    let cancelled = false;
    const ids = peerIdsKey.split(",").map(Number).filter(Boolean);

    /**
     * @returns {Promise<void>}
     */
    async function fetchPeers() {
      const results = await Promise.all(
        ids.map(async (peerId) => {
          try {
            const result = await getUser(userId, peerId);
            if (result.success && result.data) {
              return { peerId, user: result.data };
            }
          } catch (err) {
            console.error("Failed to fetch user:", peerId, err);
          }
          return null;
        })
      );
      if (cancelled) return;
      setPeerUsers((prev) => {
        const next = { ...prev };
        results.forEach((row) => {
          if (row) next[row.peerId] = row.user;
        });
        return next;
      });
    }

    fetchPeers();
    return () => {
      cancelled = true;
    };
  }, [userId, peerIdsKey]);

  const activeConnections = useMemo(
    () =>
      pinHighlightedFirst(
        connections.filter(
          (conn) => conn.status === "active" || conn.status === "accepted"
        ),
        highlightedConnectionId
      ),
    [connections, highlightedConnectionId]
  );

  const sentConnections = useMemo(
    () =>
      pinHighlightedFirst(
        connections.filter(
          (conn) => conn.status === "pending" && Number(conn.sender_id) === Number(userId)
        ),
        highlightedConnectionId
      ),
    [connections, userId, highlightedConnectionId]
  );

  const receivedConnections = useMemo(
    () =>
      pinHighlightedFirst(
        connections.filter(
          (conn) =>
            (conn.status === "pending" && Number(conn.receiver_id) === Number(userId)) ||
            acceptedIds.has(conn.id)
        ),
        highlightedConnectionId
      ),
    [connections, userId, acceptedIds, highlightedConnectionId]
  );

  /**
   * @param {string} nextTab
   * @returns {void}
   */
  const handleTabChange = (nextTab) => {
    const next = {};
    if (nextTab !== "all") {
      next.tab = nextTab;
    } else if (highlightedConnectionId) {
      next.tab = "all";
    }
    if (highlightedConnectionId) {
      next.connection = String(highlightedConnectionId);
    }
    setSearchParams(next, { replace: true });
  };

  /**
   * @returns {Promise<void>}
   */
  const handleConfirm = async () => {
    if (!confirm || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (confirm.type === "remove" || confirm.type === "withdraw") {
        const result = await deleteConnection(userId, confirm.peerId);
        if (result.success) {
          reportConnectionChange({
            peerUserId: confirm.peerId,
            status: "none",
            connectionId: 0,
          });
          setConfirm(null);
          await reloadConnections();
          await refreshPendingConnections();
        } else {
          setError(result.error || "Failed to update connection");
        }
      } else if (confirm.type === "decline") {
        const result = await respondConnection({
          connection_id: confirm.connection.id,
          user_id: userId,
          status: "rejected",
        });
        if (result.success) {
          reportConnectionChange({
            peerUserId: confirm.peerId,
            status: "rejected",
            connectionId: confirm.connection.id,
          });
          setConfirm(null);
          await reloadConnections();
          await refreshPendingConnections();
        } else {
          setError(result.error || "Failed to decline connection");
        }
      }
    } catch (err) {
      setError("Network error while updating connection");
    } finally {
      setBusy(false);
    }
  };

  if (!userId) {
    return (
      <SignedOutPrompt
        title="Manage connections"
        description="Sign in to view your connections and respond to invitations."
      />
    );
  }

  const confirmCopy = {
    remove: {
      title: "Remove connection?",
      description: `Are you sure you want to remove your connection with ${confirm?.name || "this user"}?`,
      action: "Remove",
    },
    withdraw: {
      title: "Withdraw request?",
      description: `Are you sure you want to withdraw your connection request to ${confirm?.name || "this user"}?`,
      action: "Withdraw",
    },
    decline: {
      title: "Decline request?",
      description: `Are you sure you want to decline this connection request from ${confirm?.name || "this user"}?`,
      action: "Decline",
    },
  }[confirm?.type] || { title: "", description: "", action: "" };

  /**
   * @param {object} connection
   * @returns {JSX.Element}
   */
  const renderRow = (connection, actions, metaPrefix) => {
    const peerId = peerIdFor(connection, userId);
    const person = peerUsers[peerId];
    const meta = connection.created_at
      ? `${metaPrefix} ${new Date(connection.created_at).toLocaleDateString()}`
      : null;
    return (
      <li key={connection.id}>
        <ConnectionRow
          person={person}
          personId={peerId}
          meta={meta}
          highlighted={Number(connection.id) === highlightedConnectionId}
          actions={actions(connection, peerId, displayName(person))}
        />
      </li>
    );
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-6 pb-12">
      <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm">
        <Link
          to="/network"
          className="font-medium text-accent underline underline-offset-2 hover:text-text-accent-hover"
        >
          Network
        </Link>
        <span className="text-text-muted" aria-hidden>
          <ChevronIcon />
        </span>
        <span className="text-text-secondary" aria-current="page">
          Manage connections
        </span>
      </nav>

      <div>
        <h1 className="font-display text-2xl font-medium text-text-primary sm:text-3xl">
          Manage connections
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Review people you are connected with, withdraw sent requests, and respond to invitations.
        </p>
      </div>

      {error ? <NotificationErrorPanel message={error} /> : null}

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList className="mb-4 grid w-full grid-cols-3">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
          <TabsTrigger value="received" className="gap-2">
            Received
            {pendingConnectionCount > 0 ? (
              <Badge variant="secondary" className="h-5 min-w-5 px-1.5">
                {pendingConnectionCount > 9 ? "9+" : pendingConnectionCount}
              </Badge>
            ) : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {activeConnections.length === 0 ? (
            <NotificationEmptyState
              title="No connections yet"
              subtitle="People you accept will appear here."
            />
          ) : (
            <ul className="list-none space-y-4 p-0">
              {activeConnections.map((connection) =>
                renderRow(connection, (conn, peerId, name) => (
                  <RemoveOverflowMenu
                    onRemove={() =>
                      setConfirm({ type: "remove", connection: conn, peerId, name })
                    }
                  />
                ), "Connected")
              )}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="sent">
          {sentConnections.length === 0 ? (
            <NotificationEmptyState
              title="No sent requests"
              subtitle="Connection requests you send will appear here until they are accepted."
            />
          ) : (
            <ul className="list-none space-y-4 p-0">
              {sentConnections.map((connection) =>
                renderRow(
                  connection,
                  (conn, peerId, name) => (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={busy}
                      onClick={() =>
                        setConfirm({ type: "withdraw", connection: conn, peerId, name })
                      }
                    >
                      Withdraw
                    </Button>
                  ),
                  "Requested"
                )
              )}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="received">
          {receivedConnections.length === 0 ? (
            <NotificationEmptyState
              title="No pending connection requests"
              subtitle="You are all caught up."
            />
          ) : (
            <ul className="list-none space-y-4 p-0">
              {receivedConnections.map((connection) =>
                renderRow(
                  connection,
                  (conn, peerId, name) => {
                    const justAccepted = acceptedIds.has(conn.id);
                    return (
                      <>
                        <PostConnectButton
                          userId={userId}
                          user={user}
                          acceptLabel="Accept"
                          item={{
                            author_id: peerId,
                            author_is_org: false,
                            user_connection_status: justAccepted
                              ? "active"
                              : "pending_received",
                            connection_id: conn.id,
                          }}
                          onAccepted={() => {
                            setAcceptedIds((prev) => new Set(prev).add(conn.id));
                          }}
                          onAcceptedHidden={async () => {
                            await reloadConnections();
                            setAcceptedIds((prev) => {
                              const next = new Set(prev);
                              next.delete(conn.id);
                              return next;
                            });
                          }}
                        />
                        {justAccepted ? null : (
                          <Button
                            type="button"
                            variant="outline"
                            disabled={busy}
                            onClick={() =>
                              setConfirm({
                                type: "decline",
                                connection: conn,
                                peerId,
                                name,
                              })
                            }
                          >
                            Decline
                          </Button>
                        )}
                      </>
                    );
                  },
                  "Requested"
                )
              )}
            </ul>
          )}
        </TabsContent>
      </Tabs>

      <Dialog
        open={Boolean(confirm)}
        onOpenChange={(open) => {
          if (!open && !busy) setConfirm(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{confirmCopy.title}</DialogTitle>
            <DialogDescription>{confirmCopy.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => setConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={busy}
              onClick={handleConfirm}
            >
              {busy ? "Loading…" : confirmCopy.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ManageConnectionsPage;
